import Link from 'next/link';
import React from 'react';

import styled from '@emotion/styled';
import { useTranslations } from 'next-intl';

import { HubClasses, HubTile } from '../HubTile';

const SHubTile = styled(HubTile)`
  .${HubClasses.topRowContainer} {
    align-items: flex-start;
  }
`;

// IllustrationContainer is positioned absolutely so the asset can hug the
// real bottom edge of the tile (not just the bottom of a flex slot). It sits
// underneath the Content via z-index so the text always reads on top of it.
const IllustrationContainer = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;

  /* On a narrow tile the artwork fills the whole card and the heading lands on
     the character's face. A top-down scrim keeps the text readable at every
     width without moving the illustration. */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      180deg,
      rgba(12, 9, 4, 0.9) 0%,
      rgba(12, 9, 4, 0.7) 40%,
      rgba(12, 9, 4, 0) 75%
    );
  }
`;

const CouncilImage = styled.img`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 36rem;
  height: auto;
  object-fit: contain;
  user-select: none;
  -webkit-user-drag: none;
  pointer-events: none;
`;

const Content = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  gap: ${({ theme }) => theme.spacing('1500')};
  padding-top: 3rem;
  z-index: 1;
`;

const TextContainer = styled.div`
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-self: stretch;
  max-width: 32rem;
  gap: ${({ theme }) => theme.spacing('0750')};
`;

const Subtitle = styled.h3`
  color: ${({ theme }) => theme.color.light};
  font: ${({ theme }) => theme.typography.body.bold};
  font-size: ${({ theme }) => theme.typescale.size['4']};
  line-height: ${({ theme }) => theme.typescale.lineHeight.condensed};
`;

const Description = styled.p`
  color: ${({ theme }) => theme.color.light};
  font: ${({ theme }) => theme.typography.body.regular};
  font-size: ${({ theme }) => theme.typescale.size['1']};
  line-height: ${({ theme }) => theme.typescale.lineHeight.extended};
  opacity: ${({ theme }) => theme.opacity.dense};
`;

const SHubTileStyled = styled(SHubTile)`
  background:
    radial-gradient(
      120% 60% at 50% 100%,
      rgba(255, 200, 130, 0.18) 0%,
      transparent 60%
    ),
    #1a140b;
  color: ${({ theme }) => theme.color.light};

  /* Stacked single-column, the tile otherwise collapses to the height of its
     three lines of text and the artwork has nowhere to go. */
  ${({ theme }) => theme.breakpoints.down('sm')} {
    min-height: 22rem;
  }
`;

export const CouncilTile = () => {
  const t = useTranslations();

  return (
    <Link href="/council">
      <SHubTileStyled
        textColor="light"
        stateLayer="light"
        title={t('COUNCIL_HUB_TILE_TITLE')}
      >
        <IllustrationContainer>
          <CouncilImage src="/council-assets/tile-council-hub.png" alt="" />
        </IllustrationContainer>

        <Content>
          <TextContainer>
            <Subtitle>{t('COUNCIL_HUB_TILE_SUBTITLE')}</Subtitle>
            <Description>{t('COUNCIL_HUB_TILE_DESCRIPTION')}</Description>
          </TextContainer>
        </Content>
      </SHubTileStyled>
    </Link>
  );
};
